import {
    Box,
    Button,
    Card,
    Stack,
    TextField,
    Typography,
    Alert
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import EventIcon from '@mui/icons-material/Event';
import BarChartIcon from '@mui/icons-material/BarChart';
import TimelineIcon from '@mui/icons-material/Timeline';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import PercentIcon from '@mui/icons-material/Percent';
import {
    apiGetTradingLimits,
    apiSaveTradingLimits
} from '../../../store/feature/eex/eexSlice';

export default function Limits() {
    const dispatch = useDispatch();
    const { isSaveTradingLimitsLoading } = useSelector((state) => state.eex);

    const limitFields = [
        { key: 'daily_limit', label: 'Daily Limit', icon: EventIcon },
        { key: 'weekly_limit', label: 'Weekly Limit', icon: BarChartIcon },
        { key: 'monthly_limit', label: 'Monthly Limit', icon: TimelineIcon },
        { key: 'quarterly_limit', label: 'Quarterly Limit', icon: QueryStatsIcon },
        { key: 'yearly_limit', label: 'Yearly Limit', icon: EmojiEventsIcon }
    ];

    const [limits, setLimits] = useState({
        daily_limit: '',
        weekly_limit: '',
        monthly_limit: '',
        quarterly_limit: '',
        yearly_limit: ''
    });
    const [maxPriceDifference, setMaxPriceDifference] = useState('');
    const [errors, setErrors] = useState({});
    const [statusMessage, setStatusMessage] = useState('');
    const [statusType, setStatusType] = useState('');

    useEffect(() => {
        const savedLimits = localStorage.getItem('trading_limits');
        if (savedLimits) {
            const parsedLimits = JSON.parse(savedLimits);
            setLimits({
                daily_limit: parsedLimits.daily_limit || '',
                weekly_limit: parsedLimits.weekly_limit || '',
                monthly_limit: parsedLimits.monthly_limit || '',
                quarterly_limit: parsedLimits.quarterly_limit || '',
                yearly_limit: parsedLimits.yearly_limit || ''
            });
            setMaxPriceDifference(parsedLimits.max_price_difference || '');
        }
    }, []);

    useEffect(() => {
        (async () => {
            const result = await dispatch(apiGetTradingLimits());
            if (apiGetTradingLimits.fulfilled.match(result)) {
                const data = result.payload?.data || {};
                setLimits({
                    daily_limit: data.daily_limit ?? '',
                    weekly_limit: data.weekly_limit ?? '',
                    monthly_limit: data.monthly_limit ?? '',
                    quarterly_limit: data.quarterly_limit ?? '',
                    yearly_limit: data.yearly_limit ?? ''
                });
                setMaxPriceDifference(data.max_price_difference ?? '');
                localStorage.setItem('trading_limits', JSON.stringify(data));
            }
        })();
    }, [dispatch]);

    const handleChange = (key) => (event) => {
        const nextValue = event.target.value;
        if (nextValue !== '' && !/^\d*\.?\d*$/.test(nextValue)) {
            return;
        }

        setLimits((prev) => ({ ...prev, [key]: nextValue }));
        setErrors((prev) => ({ ...prev, [key]: '' }));
        setStatusMessage('');
    };

    const handleMaxPriceDifferenceChange = (event) => {
        const nextValue = event.target.value;
        if (nextValue !== '' && !/^\d*\.?\d*$/.test(nextValue)) {
            return;
        }
        setMaxPriceDifference(nextValue);
        setErrors((prev) => ({ ...prev, max_price_difference: '' }));
        setStatusMessage('');
    };

    const validate = () => {
        const nextErrors = {};

        limitFields.forEach(({ key }) => {
            if (limits[key] === '') {
                nextErrors[key] = 'Required';
            }
        });

        if (maxPriceDifference === '') {
            nextErrors.max_price_difference = 'Required';
        }

        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) {
            return;
        }

        setStatusMessage('');

        const payload = {
            provider: 'eex',
            ...Object.fromEntries(
                Object.entries(limits).map(([key, value]) => [
                    key,
                    parseFloat(value)
                ])
            ),
            max_price_difference: parseFloat(maxPriceDifference)
        };

        const result = await dispatch(apiSaveTradingLimits(payload));

        if (apiSaveTradingLimits.fulfilled.match(result)) {
            setStatusMessage('✓ Limits saved successfully!');
            setStatusType('success');
            // save to local storage and populate the fields on load
            localStorage.setItem('trading_limits', JSON.stringify(payload));
            // setLimits({
            //     daily_limit: '',
            //     weekly_limit: '',
            //     monthly_limit: '',
            //     quarterly_limit: '',
            //     yearly_limit: ''
            // });
            setMaxPriceDifference('');
        } else {
            setStatusMessage(
                result.payload?.message ||
                    '✗ Unable to save limits. Please try again.'
            );
            setStatusType('error');
        }
    };

    return (
        <Box
            sx={{
                p: 3,
                // maxWidth: 700,
                maxWidth: '100%',
                width: '100%',
                background: 'linear-gradient(135deg, #f5f7fa 0%, #f0f4f8 100%)',
                borderRadius: 2
            }}
        >
            {/* Header */}
            <Box sx={{ mb: 4 }}>
                <Typography
                    variant="h5"
                    sx={{
                        fontWeight: 700,
                        background:
                            'linear-gradient(135deg, #0b9cfd 0%, #3DC3C1 100%)',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        mb: 1
                    }}
                >
                    Trading Limits
                </Typography>
            </Box>

            {/* Limit Fields Card */}
            <Card
                sx={{
                    p: 3,
                    mb: 3,
                    background: '#fff',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                    border: '1px solid #E8F0FE'
                }}
            >
                <Box
                    sx={{
                        mb: 1,
                        backgroundColor: '#cbf0e6',
                        p: 2,
                        borderRadius: 1
                    }}
                >
                    <Typography variant="body2" color="text.secondary">
                        Set your trading limits to control risk exposure across
                        different timeframes
                    </Typography>
                </Box>

                <Stack spacing={2.5}>
                    {limitFields.map(({ key, label, icon: IconComponent }) => (
                        <Box
                            key={key}
                            sx={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: 2.5,
                                p: 2,
                                // backgroundColor: errors[key] ? '#FFF3E0' : '#F8FCFF',
                                backgroundColor: '#F8FCFF',
                                borderRadius: 1.5,
                                border: `1px solid ${
                                    errors[key] ? '#FFE0B2' : '#CBF0E6'
                                }`,
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    backgroundColor: errors[key]
                                        ? '#FFF3E0'
                                        : '#F0FBFF',
                                    borderColor: '#3DC3C1'
                                }
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    minWidth: '40px',
                                    height: '40px',
                                    borderRadius: '8px',
                                    backgroundColor: '#E0F2F1',
                                    color: '#00796B',
                                    flexShrink: 0
                                }}
                            >
                                <IconComponent sx={{ fontSize: '1.25rem' }} />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <Typography
                                    sx={{
                                        fontWeight: 600,
                                        color: '#1a3a52',
                                        mb: 1,
                                        fontSize: '0.95rem'
                                    }}
                                >
                                    {label}
                                </Typography>
                                <TextField
                                    fullWidth
                                    value={limits[key]}
                                    onChange={handleChange(key)}
                                    placeholder="Enter limit amount"
                                    size="small"
                                    type="number"
                                    error={Boolean(errors[key])}
                                    helperText={errors[key] || ''}
                                    inputProps={{
                                        inputMode: 'numeric',
                                        min: 0
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            backgroundColor: '#fff',
                                            borderRadius: 1,
                                            transition: 'all 0.2s ease',
                                            '&:hover fieldset': {
                                                borderColor: '#3DC3C1'
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: '#0b9cfd',
                                                boxShadow: '0 0 0 2px #CBF0E6'
                                            }
                                        },
                                        '& .MuiOutlinedInput-input': {
                                            padding: '10px 12px',
                                            fontSize: '0.95rem'
                                        }
                                    }}
                                />
                            </Box>
                        </Box>
                    ))}
                </Stack>

                {/* Max Price Difference Percent */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 2.5,
                        p: 2,
                        mt: 2.5,
                        backgroundColor: '#F8FCFF',
                        borderRadius: 1.5,
                        border: `1px solid ${
                            errors.max_price_difference
                                ? '#FFE0B2'
                                : '#CBF0E6'
                        }`,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                            backgroundColor: errors.max_price_difference
                                ? '#FFF3E0'
                                : '#F0FBFF',
                            borderColor: '#3DC3C1'
                        }
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minWidth: '40px',
                            height: '40px',
                            borderRadius: '8px',
                            backgroundColor: '#E0F2F1',
                            color: '#00796B',
                            flexShrink: 0
                        }}
                    >
                        <PercentIcon sx={{ fontSize: '1.25rem' }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Typography
                            sx={{
                                fontWeight: 600,
                                color: '#1a3a52',
                                mb: 1,
                                fontSize: '0.95rem'
                            }}
                        >
                            Max Price Difference
                        </Typography>
                        <TextField
                            fullWidth
                            value={maxPriceDifference}
                            onChange={handleMaxPriceDifferenceChange}
                            placeholder="Enter max price difference"
                            size="small"
                            type="number"
                            error={Boolean(errors.max_price_difference)}
                            helperText={errors.max_price_difference || ''}
                            inputProps={{
                                inputMode: 'decimal',
                                min: 0,
                                step: 0.1
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    backgroundColor: '#fff',
                                    borderRadius: 1,
                                    transition: 'all 0.2s ease',
                                    '&:hover fieldset': {
                                        borderColor: '#3DC3C1'
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: '#0b9cfd',
                                        boxShadow: '0 0 0 2px #CBF0E6'
                                    }
                                },
                                '& .MuiOutlinedInput-input': {
                                    padding: '10px 12px',
                                    fontSize: '0.95rem'
                                }
                            }}
                        />
                    </Box>
                </Box>
            </Card>

            {/* Status Area */}
            {statusMessage && (
                <Alert
                    severity={statusType}
                    icon={
                        statusType === 'success' ? (
                            <CheckCircleIcon sx={{ color: '#4CAF50' }} />
                        ) : (
                            <ErrorIcon sx={{ color: '#F44336' }} />
                        )
                    }
                    sx={{
                        mb: 2,
                        backgroundColor:
                            statusType === 'success' ? '#E8F5E9' : '#FFEBEE',
                        color: statusType === 'success' ? '#2E7D32' : '#C62828',
                        border: `1px solid ${
                            statusType === 'success' ? '#C8E6C9' : '#FFCDD2'
                        }`
                    }}
                >
                    {statusMessage}
                </Alert>
            )}

            {/* Action Button */}
            <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={isSaveTradingLimitsLoading}
                    sx={{
                        // background:
                        //     'linear-gradient(135deg, #0b9cfd 0%, #3DC3C1 100%)',
                        backgroundColor: '#0b9cfd',
                        color: '#fff',
                        fontWeight: 600,
                        px: 3,
                        py: 1.2,
                        borderRadius: 1,
                        textTransform: 'none',
                        fontSize: '0.95rem',
                        transition: 'all 0.3s ease',
                        boxShadow: '0 4px 12px rgba(11, 156, 253, 0.3)',
                        '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 6px 16px rgba(11, 156, 253, 0.4)'
                        },
                        '&:disabled': {
                            background: '#CCCCCC',
                            boxShadow: 'none'
                        },
                        marginLeft: 'auto'
                    }}
                >
                    {isSaveTradingLimitsLoading ? 'Saving...' : 'Save Limits'}
                </Button>
            </Box>

            {/* Info Footer */}
            <Box
                sx={{
                    mt: 3,
                    p: 2,
                    backgroundColor: '#E0F2F1',
                    borderRadius: 1.5,
                    border: '1px solid #B2DFDB'
                }}
            >
                <Typography
                    variant="caption"
                    sx={{ color: '#00695c', lineHeight: 1.6 }}
                >
                    💡 <strong>Tip:</strong> Set higher limits for longer
                    timeframes to allow more flexibility. These limits help
                    manage your risk exposure.
                </Typography>
            </Box>
        </Box>
    );
}
